/**
 * The roster. The centrepiece of the boss shell.
 *
 * Everything that changes data lives here, next to the query cache, so the
 * optimistic patching is in one readable place rather than spread through cell
 * components. Three mutations cover the whole grid: create (a template dropped
 * on a cell), move (an existing shift dropped on a different cell, which is
 * also how a shift is reassigned) and save (the drawer).
 *
 * Optimism is the point. `onMutate` patches the cached week immediately, which
 * makes both the card and the totals bar move on the same frame as the drop.
 * `onError` puts the snapshot back and says why. `onSettled` invalidates, so
 * the server remains the authority and any figure it computes differently wins
 * within a moment.
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Copy,
  LayoutGrid,
  Send,
  Users,
  Trash2,
} from 'lucide-react';
import {
  api,
  ApiError,
  qk,
  type RosterResponse,
  type Shift,
} from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import {
  addDays,
  fmtDateRange,
  fmtDayMonth,
  fmtDuration,
  fmtMoney,
  fmtTimeRange,
  today,
  weekDates as weekDatesOf,
  weekStart,
  type Hhmm,
  type IsoDate,
} from '../../lib/time.ts';
import { ErrorState, RosterSkeleton } from '../../components/Bits.tsx';
import { Modal, OutcomeModal } from '../../components/Overlay.tsx';
import { UndoBar } from '../../components/UndoBar.tsx';
import { RosterGrid, type GridGroup, type GridRow } from '../../roster/RosterGrid.tsx';
import { TemplatePalette } from '../../roster/TemplatePalette.tsx';
import { TotalsBar } from '../../roster/TotalsBar.tsx';
import { ShiftDrawer, shiftToForm, type ShiftForm } from '../../roster/ShiftDrawer.tsx';
import { CoverPanel } from '../../roster/CoverPanel.tsx';
import { WeekLanes, type LaneMove } from '../../roster/WeekLanes.tsx';
import { ShiftSheet } from '../../roster/ShiftSheet.tsx';
import { usePhone } from '../../lib/useMediaQuery.ts';
import { costRoster, ratesOf, unavailableIndex } from '../../roster/rosterMath.ts';
import { cellKey, isShiftDrag, isTemplateDrag, type CellDrop } from '../../roster/dnd.ts';

type ViewMode = 'people' | 'areas';

const OPEN_ROW: GridRow = {
  key: 'open',
  userId: null,
  areaId: null,
  name: 'Open shifts',
  sub: 'Nobody assigned yet',
  open: true,
};

export default function RosterPage() {
  // One scroll container for the week: the grid owns it, the totals bar mirrors it.
  const gridScrollRef = useRef<HTMLDivElement>(null);
  const { tenant, isOwner } = useSession();
  const slug = tenant?.tenantSlug ?? '';
  // Two layouts, not one squeezed: a phone gets the week as day lanes and a
  // bottom sheet editor, a laptop gets the people-by-days grid and the drawer.
  const phone = usePhone();
  const qc = useQueryClient();
  const [, navigate] = useLocation();

  const [week, setWeek] = useState<IsoDate>(() => weekStart(today()));
  const [view, setView] = useState<ViewMode>('people');
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [drawer, setDrawer] = useState<{ form: ShiftForm; conflict: { name: string; note: string | null } | null } | null>(
    null,
  );
  const [publishOpen, setPublishOpen] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  const [outcome, setOutcome] = useState<{ ok: boolean; title: string; message: string } | null>(null);

  const key = qk.roster(slug, week);
  const query = useQuery({ queryKey: key, queryFn: () => api.roster(slug, week), enabled: slug !== '' });
  const data = query.data;

  const dates = useMemo(() => weekDatesOf(week), [week]);
  const rates = useMemo(() => ratesOf(data?.staff ?? []), [data?.staff]);
  const unavailable = useMemo(() => unavailableIndex(data?.unavailability ?? []), [data?.unavailability]);

  // Totals recomputed from the cached shifts rather than read off the response,
  // so an optimistic card is already in the figure before the server replies.
  const totals = useMemo(() => costRoster(data?.shifts ?? [], rates), [data?.shifts, rates]);

  const patch = useCallback(
    (fn: (r: RosterResponse) => RosterResponse) => {
      qc.setQueryData<RosterResponse>(key, (old) => (old ? fn(old) : old));
    },
    [qc, key],
  );

  const snapshot = useCallback(() => qc.getQueryData<RosterResponse>(key), [qc, key]);
  const restore = useCallback(
    (snap: RosterResponse | undefined) => {
      if (snap) qc.setQueryData(key, snap);
    },
    [qc, key],
  );
  const settle = useCallback(() => {
    void qc.invalidateQueries({ queryKey: key });
  }, [qc, key]);

  // --- mutations ----------------------------------------------------------
  const createShift = useMutation({
    mutationFn: (v: { form: ShiftForm; force: boolean }) =>
      api.createShift(slug, {
        date: v.form.date,
        startTime: v.form.startTime,
        endTime: v.form.endTime,
        areaId: v.form.areaId,
        userId: v.form.userId,
        breakMinutes: v.form.breakMinutes,
        notes: v.form.notes || null,
        force: v.force,
      }),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: key });
      const snap = snapshot();
      const optimistic: Shift = {
        id: `pending-${crypto.randomUUID()}`,
        date: v.form.date,
        startTime: v.form.startTime,
        endTime: v.form.endTime,
        areaId: v.form.areaId,
        userId: v.form.userId,
        breakMinutes: v.form.breakMinutes,
        status: 'draft',
        locked: false,
        notes: v.form.notes || null,
      };
      patch((r) => ({ ...r, shifts: [...r.shifts, optimistic] }));
      return { snap };
    },
    onError: (err, _v, ctx) => {
      restore(ctx?.snap);
      reportFailure(err, 'add that shift');
    },
    onSettled: settle,
  });

  const updateShift = useMutation({
    mutationFn: (v: { id: string; changes: Partial<Shift>; force?: boolean }) =>
      api.updateShift(slug, v.id, { ...v.changes, force: v.force }),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: key });
      const snap = snapshot();
      patch((r) => ({
        ...r,
        shifts: r.shifts.map((s) => (s.id === v.id ? { ...s, ...v.changes } : s)),
      }));
      return { snap };
    },
    onError: (err, _v, ctx) => {
      restore(ctx?.snap);
      reportFailure(err, 'save that change');
    },
    onSettled: settle,
  });

  const assignCover = useMutation({
    mutationFn: async (v: { shiftId: string; userId: string }) => {
      await api.updateShift(slug, v.shiftId, { userId: v.userId, force: true });
      await api.cancelCover(slug, v.shiftId);
    },
    onSuccess: () => {
      setDrawer(null);
      void qc.invalidateQueries({ queryKey: key });
    },
    onError: (err) => reportFailure(err, 'give that shift to them'),
  });

  const coverShift = drawer?.form.id
    ? (data?.shifts.find((s) => s.id === drawer.form.id) ?? null)
    : null;

  /**
   * Deleting from the week view is one click, so the safety net is the undo
   * that follows rather than a confirm dialog standing in front of every
   * deliberate delete. The server hands back everything needed to put the shift
   * back exactly as it was, published state included.
   */
  const [undo, setUndo] = useState<{ shift: Shift; at: number } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Shift | null>(null);

  const deleteShift = useMutation({
    mutationFn: (id: string) => api.deleteShift(slug, id),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: key });
      const snap = snapshot();
      patch((r) => ({ ...r, shifts: r.shifts.filter((s) => s.id !== id) }));
      return { snap };
    },
    onError: (err, _id, ctx) => {
      restore(ctx?.snap);
      reportFailure(err, 'delete that shift');
    },
    onSuccess: (res) => {
      setDrawer(null);
      if (res?.restore) setUndo({ shift: res.restore, at: Date.now() });
    },
    onSettled: settle,
  });

  const undoDelete = useMutation({
    mutationFn: (shift: Shift) =>
      api.restoreShift(slug, {
        date: shift.date,
        startTime: shift.startTime,
        endTime: shift.endTime,
        areaId: shift.areaId,
        userId: shift.userId,
        breakMinutes: shift.breakMinutes,
        notes: shift.notes,
        status: shift.status,
        locked: shift.locked,
        needsCover: shift.needsCover,
        coverReason: shift.coverReason,
      }),
    onSuccess: () => {
      setUndo(null);
      void qc.invalidateQueries({ queryKey: key });
    },
    onError: (err) => reportFailure(err, 'put that shift back'),
  });

  // Deleting straight from the grid, where the boss has not opened the shift
  // and read what is in it. A locked shift never offers the button at all, so
  // this only guards the case where the roster is already published: the team
  // has seen that shift and may have planned around it.
  const askDeleteShift = useCallback((id: string) => {
    const shift = (data?.shifts ?? []).find((s) => s.id === id);
    if (!shift) return;
    if (shift.status === 'published') {
      setConfirmDelete(shift);
      return;
    }
    deleteShift.mutate(id);
  }, [data?.shifts, deleteShift]);

  const publish = useMutation({
    mutationFn: () => api.publishWeek(slug, week),
    onSuccess: (res) => {
      setPublishOpen(false);
      setOutcome({
        ok: true,
        title: 'Week published',
        message:
          res.count === 0
            ? 'There were no draft shifts left to publish. The team already has this week.'
            : `${res.count} ${res.count === 1 ? 'shift is' : 'shifts are'} now live. Everyone rostered has been notified.`,
      });
    },
    onError: (err) => {
      setPublishOpen(false);
      reportFailure(err, 'publish the week');
    },
    onSettled: settle,
  });

  const copyWeek = useMutation({
    mutationFn: () => api.copyWeek(slug, addDays(week, -7), week),
    onSuccess: (res) => {
      setCopyOpen(false);
      const madeOpen = res.madeOpen?.length ?? 0;
      setOutcome({
        ok: true,
        title: 'Last week copied',
        message:
          `${res.copied} ${res.copied === 1 ? 'shift was' : 'shifts were'} copied in as drafts.` +
          (madeOpen > 0
            ? ` ${madeOpen} could not go to the same person because they are unavailable, so ${
                madeOpen === 1 ? 'it is' : 'they are'
              } waiting on the Open shifts row.`
            : ' Nothing clashed with anybody who is unavailable.'),
      });
    },
    onError: (err) => {
      setCopyOpen(false);
      reportFailure(err, 'copy last week');
    },
    onSettled: settle,
  });

  function reportFailure(err: unknown, what: string) {
    const e = err instanceof ApiError ? err : null;
    setOutcome({
      ok: false,
      title: `Could not ${what}`,
      message: e?.message ?? `Something went wrong and we could not ${what}. Nothing was changed.`,
    });
  }

  // --- grid model ---------------------------------------------------------
  const shiftsByCell = useMemo(() => {
    const map = new Map<string, Shift[]>();
    for (const s of data?.shifts ?? []) {
      // In the People view every shift sits on its person's row regardless of
      // area; in the Areas view the row is the (area, person) pair.
      const areaKey = view === 'areas' ? s.areaId : null;
      const k = cellKey(s.userId, areaKey, s.date);
      const list = map.get(k);
      if (list) list.push(s);
      else map.set(k, [s]);
    }
    for (const list of map.values()) list.sort((a, b) => a.startTime.localeCompare(b.startTime));
    return map;
  }, [data?.shifts, view]);

  // Areas the business actually uses. A tenant with one area (or none) has
  // nothing to group by, so the Area view is not offered.
  const activeAreas = useMemo(() => (data?.areas ?? []).filter((a) => a.active !== false), [data?.areas]);

  const groups: GridGroup[] = useMemo(() => {
    const staff = (data?.staff ?? []).filter((s) => s.active !== false);
    const rowFor = (userId: string, name: string, role: string, areaId: string | null): GridRow => ({
      key: `${areaId ?? 'none'}|${userId}`,
      userId,
      areaId,
      name,
      sub: role === 'owner' ? 'Owner' : role === 'manager' ? 'Manager' : 'Team member',
    });

    if (view === 'people') {
      return [
        { key: 'open', title: null, rows: [OPEN_ROW] },
        {
          key: 'people',
          title: null,
          rows: staff.map((s) => rowFor(s.userId, s.name, s.role, null)),
        },
      ];
    }

    const areas = [...(data?.areas ?? [])].filter((a) => a.active !== false).sort((a, b) => a.position - b.position);
    const out: GridGroup[] = [
      { key: 'open', title: 'Open shifts', rows: [OPEN_ROW] },
      ...areas.map((a) => ({
        key: a.id,
        title: a.name,
        rows: staff.map((s) => ({ ...rowFor(s.userId, s.name, s.role, a.id), key: `${a.id}|${s.userId}` })),
      })),
      {
        key: 'no-area',
        title: 'No area',
        rows: staff.map((s) => rowFor(s.userId, s.name, s.role, null)),
      },
    ];
    return out;
  }, [data?.staff, data?.areas, view]);

  // --- drop handling ------------------------------------------------------
  const openConflict = useCallback(
    (userId: string | null, date: IsoDate) => {
      if (!userId) return null;
      if (!unavailable.has(userId, date)) return null;
      const person = (data?.staff ?? []).find((s) => s.userId === userId);
      return { name: person?.name ?? 'This person', note: unavailable.noteFor(userId, date) };
    },
    [unavailable, data?.staff],
  );

  const addShift = useCallback(
    (target: CellDrop) => {
      const tpl = (data?.templates ?? []).find((t) => t.active !== false);
      const form: ShiftForm = {
        id: null,
        date: target.date,
        startTime: tpl ? tpl.startTime.slice(0, 5) : '09:00',
        endTime: tpl ? tpl.endTime.slice(0, 5) : '17:00',
        userId: target.userId,
        areaId: target.areaId,
        breakMinutes: 0,
        notes: '',
        locked: false,
        status: 'draft',
      };
      setDrawer({ form, conflict: openConflict(target.userId, target.date) });
    },
    [data?.templates, openConflict],
  );

  const placeTemplate = useCallback(
    (target: CellDrop, templateId: string) => {
      const tpl = (data?.templates ?? []).find((t) => t.id === templateId);
      if (!tpl) return;
      const form: ShiftForm = {
        id: null,
        date: target.date,
        startTime: tpl.startTime.slice(0, 5),
        endTime: tpl.endTime.slice(0, 5),
        userId: target.userId,
        areaId: target.areaId,
        breakMinutes: 0,
        notes: '',
        locked: false,
        status: 'draft',
      };
      const conflict = openConflict(target.userId, target.date);
      if (conflict) {
        // Not refused, and not silently allowed either. The drawer states the
        // clash and offers the override, which is the only place a boss can
        // knowingly roster over somebody's unavailability.
        setDrawer({ form, conflict });
        return;
      }
      createShift.mutate({ form, force: false });
    },
    [data?.templates, openConflict, createShift],
  );

  const onDropOnCell = useCallback(
    (target: CellDrop, source: unknown) => {
      if (isTemplateDrag(source)) {
        placeTemplate(target, source.templateId);
        return;
      }
      if (!isShiftDrag(source)) return;
      const shift = (data?.shifts ?? []).find((s) => s.id === source.shiftId);
      if (!shift) return;

      const unchanged =
        shift.date === target.date &&
        shift.userId === target.userId &&
        (view === 'people' || shift.areaId === target.areaId);
      if (unchanged) return;

      if (shift.locked) {
        setOutcome({
          ok: false,
          title: 'That shift is locked',
          message:
            'Locked shifts stay put. Open it and unlock it first if you really do need to move it.',
        });
        return;
      }

      const conflict = openConflict(target.userId, target.date);
      const changes: Partial<Shift> = {
        date: target.date,
        userId: target.userId,
        ...(view === 'areas' ? { areaId: target.areaId } : {}),
      };
      if (conflict) {
        setDrawer({ form: { ...shiftToForm(shift), ...changes, notes: shift.notes ?? '' }, conflict });
        return;
      }
      updateShift.mutate({ id: shift.id, changes });
    },
    [data?.shifts, view, openConflict, placeTemplate, updateShift],
  );

  const onPlaceTemplate = useCallback(
    (target: CellDrop) => {
      if (selectedTemplate) placeTemplate(target, selectedTemplate);
    },
    [selectedTemplate, placeTemplate],
  );

  // --- lanes (phone) --------------------------------------------------------
  // A tap on a coverage gap arrives with the exact times of the hole; a tap on
  // empty lane space arrives with none and starts from the first template.
  const addShiftLane = useCallback(
    (date: IsoDate, times?: { startTime: Hhmm; endTime: Hhmm }) => {
      const tpl = (data?.templates ?? []).find((t) => t.active !== false);
      const form: ShiftForm = {
        id: null,
        date,
        startTime: times?.startTime ?? (tpl ? tpl.startTime.slice(0, 5) : '09:00'),
        endTime: times?.endTime ?? (tpl ? tpl.endTime.slice(0, 5) : '17:00'),
        userId: null,
        areaId: null,
        breakMinutes: 0,
        notes: '',
        locked: false,
        status: 'draft',
      };
      setDrawer({ form, conflict: null });
    },
    [data?.templates],
  );

  // A drag in the lanes can change the day and the start together. The same
  // rules as a grid drop apply: locked stays put, and a landing on somebody's
  // day off is stated in the editor rather than refused or waved through.
  const moveShiftLane = useCallback(
    (id: string, to: LaneMove) => {
      const shift = (data?.shifts ?? []).find((s) => s.id === id);
      if (!shift) return;
      if (shift.locked) {
        setOutcome({
          ok: false,
          title: 'That shift is locked',
          message: 'Locked shifts stay put. Open it and unlock it first if you really do need to move it.',
        });
        return;
      }
      const changes: Partial<Shift> = { date: to.date, startTime: to.startTime, endTime: to.endTime };
      const conflict = openConflict(shift.userId, to.date);
      if (conflict) {
        setDrawer({ form: { ...shiftToForm(shift), ...changes, notes: shift.notes ?? '' }, conflict });
        return;
      }
      updateShift.mutate({ id: shift.id, changes });
    },
    [data?.shifts, openConflict, updateShift],
  );

  const onOpenShift = useCallback(
    (id: string) => {
      const shift = (data?.shifts ?? []).find((s) => s.id === id);
      if (!shift) return;
      setDrawer({ form: shiftToForm(shift), conflict: openConflict(shift.userId, shift.date) });
    },
    [data?.shifts, openConflict],
  );

  // --- publish summary ----------------------------------------------------
  const draftSummary = useMemo(() => {
    const drafts = (data?.shifts ?? []).filter((s) => s.status === 'draft');
    const t = costRoster(drafts, rates);
    return { count: drafts.length, cost: t.totalCost, minutes: t.totalMinutes };
  }, [data?.shifts, rates]);

  const openCount = (data?.shifts ?? []).filter((s) => !s.userId).length;

  if (query.isLoading) return <RosterSkeleton />;
  if (query.isError || !data) {
    return <ErrorState error={query.error} what="the roster" onRetry={() => void query.refetch()} />;
  }

  const dialogs = (
    <>
      <Modal
        open={publishOpen}
        onClose={() => setPublishOpen(false)}
        title="Publish this week?"
        subtitle={fmtDateRange(week, addDays(week, 6))}
        footer={
          <>
            <button type="button" className="btn" onClick={() => setPublishOpen(false)}>
              Not yet
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => publish.mutate()}
              disabled={publish.isPending}
            >
              <Send size={15} aria-hidden="true" />
              Publish and notify
            </button>
          </>
        }
      >
        <div className="grid grid-cols-3 gap-3">
          <div className="card p-3">
            <div className="text-[11px] font-bold uppercase tracking-wide text-faint">Shifts</div>
            <div className="tabular text-[22px] font-extrabold text-ink">{draftSummary.count}</div>
          </div>
          <div className="card p-3">
            <div className="text-[11px] font-bold uppercase tracking-wide text-faint">Hours</div>
            <div className="tabular text-[22px] font-extrabold text-ink">{fmtDuration(draftSummary.minutes)}</div>
          </div>
          <div className="card p-3">
            <div className="text-[11px] font-bold uppercase tracking-wide text-faint">Wages</div>
            <div className="tabular text-[22px] font-extrabold" style={{ color: 'var(--sp-accent-strong)' }}>
              {fmtMoney(draftSummary.cost, { cents: false })}
            </div>
          </div>
        </div>
        <p className="mt-4 text-[13.5px] leading-relaxed text-muted">
          Everyone rostered on a draft shift gets a notification. The wage figure covers the shifts being published
          now, not the whole week.
        </p>
        {openCount > 0 ? (
          <p className="mt-2 text-[13px] leading-relaxed" style={{ color: 'var(--sp-warn-strong)' }}>
            {openCount} {openCount === 1 ? 'shift is' : 'shifts are'} still on the Open shifts row with nobody
            assigned. Publishing does not fill them.
          </p>
        ) : null}
      </Modal>

      <Modal
        open={copyOpen}
        onClose={() => setCopyOpen(false)}
        title="Copy last week into this one?"
        subtitle={`From ${fmtDateRange(addDays(week, -7), addDays(week, -1))}`}
        footer={
          <>
            <button type="button" className="btn" onClick={() => setCopyOpen(false)}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => copyWeek.mutate()}
              disabled={copyWeek.isPending}
            >
              <Copy size={15} aria-hidden="true" />
              Copy the shifts
            </button>
          </>
        }
      >
        <p className="text-[13.5px] leading-relaxed text-muted">
          Every shift from last week comes across as a draft, so nothing reaches the team until you publish. Anyone
          who has marked one of these days unavailable is skipped, and their shift arrives on the Open shifts row
          for you to fill.
        </p>
        {data.shifts.length > 0 ? (
          <p className="mt-3 text-[13px] leading-relaxed" style={{ color: 'var(--sp-warn-strong)' }}>
            This week already has {data.shifts.length} {data.shifts.length === 1 ? 'shift' : 'shifts'} on it. The
            copies are added on top rather than replacing them.
          </p>
        ) : null}
      </Modal>

      {/* Deleting a shift the team has already seen is not the same as tidying
          up a draft, so that one case asks first. */}
      <Modal
        open={confirmDelete !== null}
        onClose={() => setConfirmDelete(null)}
        title="Delete a published shift"
        subtitle={
          confirmDelete
            ? `${fmtDayMonth(confirmDelete.date)}, ${fmtTimeRange(confirmDelete.startTime, confirmDelete.endTime)}`
            : undefined
        }
        footer={
          <>
            <button type="button" className="btn" onClick={() => setConfirmDelete(null)}>
              Keep it
            </button>
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => {
                if (confirmDelete) deleteShift.mutate(confirmDelete.id);
                setConfirmDelete(null);
              }}
            >
              <Trash2 size={15} aria-hidden="true" />
              Delete it
            </button>
          </>
        }
      >
        <p className="text-[13.5px] leading-relaxed text-muted">
          This week is published, so
          {confirmDelete?.userId
            ? ' the person rostered on has already seen this shift and may have planned around it.'
            : ' the team has already seen this shift.'}{' '}
          You can undo this straight afterwards.
        </p>
      </Modal>

      <UndoBar
        open={undo !== null}
        busy={undoDelete.isPending}
        label={
          undo
            ? `Deleted ${fmtDayMonth(undo.shift.date)}, ${fmtTimeRange(undo.shift.startTime, undo.shift.endTime)}`
            : ''
        }
        onUndo={() => undo && undoDelete.mutate(undo.shift)}
        onDismiss={() => setUndo(null)}
      />

      <OutcomeModal
        open={outcome !== null}
        onClose={() => setOutcome(null)}
        ok={outcome?.ok ?? true}
        title={outcome?.title ?? ''}
        message={outcome?.message ?? ''}
      />
    </>
  );

  if (phone) {
    return (
      <div className="flex flex-col" style={{ height: 'calc(100dvh - var(--sp-topbar) - var(--sp-phone-nav))' }}>
        {/* One row for the week, one for the actions. The wage total lives in
            the subtitle so the lanes get every pixel they can. */}
        <div className="flex items-center gap-2 px-3 pt-2.5 pb-1.5">
          <button type="button" className="btn btn-icon" onClick={() => setWeek(addDays(week, -7))} title="Previous week" aria-label="Previous week">
            <ChevronLeft size={16} aria-hidden="true" />
          </button>
          <div className="min-w-0 flex-1 text-center">
            <h1 className="truncate text-[15px] font-extrabold leading-tight text-ink">{fmtDateRange(week, addDays(week, 6))}</h1>
            <p className="tabular truncate text-[11.5px] leading-tight text-faint">
              {fmtMoney(totals.totalCost, { cents: false })} for {fmtDuration(totals.totalMinutes)}
              {draftSummary.count > 0 ? `, ${draftSummary.count} ${draftSummary.count === 1 ? 'draft' : 'drafts'}` : ', all published'}
            </p>
          </div>
          <button type="button" className="btn btn-icon" onClick={() => setWeek(addDays(week, 7))} title="Next week" aria-label="Next week">
            <ChevronRight size={16} aria-hidden="true" />
          </button>
        </div>
        <div className="grid grid-cols-[auto_1fr_1fr] items-center gap-2 px-3 pb-2" style={{ borderBottom: '1px solid var(--sp-border)' }}>
          <button type="button" className="btn btn-icon" onClick={() => setWeek(weekStart(today()))} title="This week|Jump back to the current week." aria-label="This week">
            <CalendarDays size={16} aria-hidden="true" />
          </button>
          <button type="button" className="btn min-w-0" onClick={() => setCopyOpen(true)} disabled={copyWeek.isPending}>
            <Copy size={15} aria-hidden="true" />
            Copy last week
          </button>
          <button
            type="button"
            className={`btn btn-primary min-w-0 ${draftSummary.count > 0 ? 'btn-glow' : ''}`}
            onClick={() => setPublishOpen(true)}
            disabled={draftSummary.count === 0 || publish.isPending}
          >
            <Send size={15} aria-hidden="true" />
            Publish{draftSummary.count > 0 ? ` ${draftSummary.count}` : ''}
          </button>
        </div>

        <WeekLanes
          weekDates={dates}
          shifts={data.shifts}
          staff={data.staff}
          areas={data.areas}
          rates={rates}
          totals={totals}
          unavailable={unavailable}
          onOpenShift={onOpenShift}
          onAddShift={addShiftLane}
          onMoveShift={moveShiftLane}
        />

        <ShiftSheet
          open={drawer !== null}
          form={drawer?.form ?? null}
          staff={data.staff}
          areas={data.areas}
          templates={data.templates}
          shifts={data.shifts}
          rates={rates}
          unavailable={unavailable}
          coverPanel={
            coverShift ? (
              <CoverPanel
                slug={slug}
                shift={coverShift}
                assigning={assignCover.isPending}
                onAssign={(userId) => assignCover.mutate({ shiftId: coverShift.id, userId })}
              />
            ) : null
          }
          saving={createShift.isPending || updateShift.isPending}
          deleting={deleteShift.isPending}
          isOwner={isOwner}
          onClose={() => setDrawer(null)}
          onDelete={(id) => {
            const shift = data.shifts.find((s) => s.id === id);
            if (shift?.status === 'published') {
              setDrawer(null);
              setConfirmDelete(shift);
              return;
            }
            deleteShift.mutate(id);
          }}
          onSave={(values, force) => {
            if (values.id === null) {
              createShift.mutate({ form: values, force });
            } else {
              updateShift.mutate({
                id: values.id,
                changes: {
                  date: values.date,
                  startTime: values.startTime,
                  endTime: values.endTime,
                  userId: values.userId,
                  areaId: values.areaId,
                  breakMinutes: values.breakMinutes,
                  notes: values.notes || null,
                  locked: values.locked,
                },
                force,
              });
            }
            setDrawer(null);
          }}
        />
        {dialogs}
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-var(--sp-topbar))] flex-col">
      {/* Week bar */}
      <div className="flex flex-wrap items-center gap-2 px-4 py-3" style={{ borderBottom: '1px solid var(--sp-border)' }}>
        <div className="flex items-center gap-1">
          <button
            type="button"
            className="btn btn-icon"
            onClick={() => setWeek(addDays(week, -7))}
            title="Previous week"
            aria-label="Previous week"
          >
            <ChevronLeft size={16} aria-hidden="true" />
          </button>
          <button
            type="button"
            className="btn btn-icon"
            onClick={() => setWeek(addDays(week, 7))}
            title="Next week"
            aria-label="Next week"
          >
            <ChevronRight size={16} aria-hidden="true" />
          </button>
        </div>
        <div className="min-w-0 flex-1 md:flex-none">
          <h1 className="truncate text-[16px] font-extrabold leading-tight text-ink">
            {fmtDateRange(week, addDays(week, 6))}
          </h1>
          <p className="text-[11.5px] leading-tight text-faint">
            {draftSummary.count > 0
              ? `${draftSummary.count} draft ${draftSummary.count === 1 ? 'shift' : 'shifts'} not yet published`
              : 'Everything on this week is published'}
          </p>
        </div>
        {/* One group, so it wraps as a unit rather than shedding controls onto
            lines of their own. On a phone it spans the full width and its
            members share the space, so the row meets both margins. */}
        <div
          className="grid w-full min-w-0 items-center gap-2 md:flex md:w-auto"
          style={{ gridTemplateColumns: activeAreas.length > 1 ? '1fr 1fr auto' : '1fr 1fr' }}
        >
        <label className="sr-only" htmlFor="week-picker">
          Jump to a week
        </label>
        <input
          id="week-picker"
          type="date"
          className="field h-[38px] min-w-0 px-2 py-0 md:w-[160px] md:px-3"
          value={week}
          onChange={(e) => e.target.value && setWeek(weekStart(e.target.value))}
          data-tip="Jump to a week|Pick any day and the roster snaps to that Monday."
        />
        <button
          type="button"
          className="btn min-w-0 md:flex-none"
          onClick={() => setWeek(weekStart(today()))}
        >
          <CalendarDays size={15} aria-hidden="true" />
          This week
        </button>

        {/* Grouping by area only means something when there IS more than one
            area. With a single area the toggle offered a view whose only effect
            was a band across the grid naming it. */}
        {activeAreas.length > 1 ? (
        <div className="flex h-[38px] shrink-0 items-center gap-1 p-0.5" style={{ background: 'var(--sp-surface-2)', borderRadius: 'var(--sp-radius-sm)', border: '1px solid var(--sp-border)' }}>
          <button
            type="button"
            className="btn btn-icon btn-sm btn-ghost"
            aria-pressed={view === 'people'}
            onClick={() => setView('people')}
            title="People view|One row per person, every area together."
            aria-label="People view"
            style={view === 'people' ? { background: 'var(--sp-surface)', boxShadow: 'var(--sp-shadow-1)' } : undefined}
          >
            <Users size={15} aria-hidden="true" />
          </button>
          <button
            type="button"
            className="btn btn-icon btn-sm btn-ghost"
            aria-pressed={view === 'areas'}
            onClick={() => setView('areas')}
            title="Area view|Rows grouped under each area, the way the floor actually runs."
            aria-label="Area view"
            style={view === 'areas' ? { background: 'var(--sp-surface)', boxShadow: 'var(--sp-shadow-1)' } : undefined}
          >
            <LayoutGrid size={15} aria-hidden="true" />
          </button>
        </div>
        ) : null}
        </div>

        {/* Desktop pushes the actions to the right edge. On a phone there is no
            room to push anything, so the spacer would only force a wrap. */}
        <span className="hidden flex-1 md:block" />

        <div className="grid w-full min-w-0 grid-cols-2 items-center gap-2 md:flex md:w-auto">
        <button
          type="button"
          className="btn min-w-0 md:flex-none"
          onClick={() => setCopyOpen(true)}
          disabled={copyWeek.isPending}
          data-tip="Copy last week|Brings last week's shifts in as drafts. Anyone unavailable is left as an open shift instead."
        >
          <Copy size={15} aria-hidden="true" />
          Copy last week
        </button>
        {/* Glows while drafts are waiting: the one button on this screen
            that the team is, in effect, waiting for. */}
        <button
          type="button"
          className={`btn btn-primary min-w-0 md:flex-none ${draftSummary.count > 0 ? 'btn-glow' : ''}`}
          onClick={() => setPublishOpen(true)}
          disabled={draftSummary.count === 0 || publish.isPending}
          data-tip={
            draftSummary.count === 0
              ? 'Nothing to publish|Every shift on this week is already live.'
              : `Publish ${draftSummary.count} draft shifts|The team is notified and the week appears on their phones.`
          }
        >
          <Send size={15} aria-hidden="true" />
          Publish week
        </button>
        </div>
      </div>

      <TemplatePalette
        templates={data.templates}
        selectedId={selectedTemplate}
        onSelect={setSelectedTemplate}
        onManage={() => navigate('/settings')}
      />

      <RosterGrid
        scrollRef={gridScrollRef}
        weekDates={dates}
        groups={groups}
        shiftsByCell={shiftsByCell}
        rates={rates}
        areas={data.areas}
        templates={data.templates}
        staff={data.staff}
        unavailable={unavailable}
        showCost
        templateSelected={selectedTemplate !== null}
        onOpenShift={onOpenShift}
        onDeleteShift={askDeleteShift}
        onDropOnCell={onDropOnCell}
        onPlaceTemplate={onPlaceTemplate}
        onAddShift={addShift}
      />

      <TotalsBar
        weekDates={dates}
        totals={totals}
        shiftCount={data.shifts.length}
        openCount={openCount}
        syncWith={gridScrollRef}
      />

      {/* The live row, not the drawer's form copy: the cover flag is not one of
          the fields being edited and must reflect what the server holds. */}
      <ShiftDrawer
        open={drawer !== null}
        form={drawer?.form ?? null}
        conflict={drawer?.conflict ?? null}
        coverPanel={
          coverShift ? (
            <CoverPanel
              slug={slug}
              shift={coverShift}
              assigning={assignCover.isPending}
              onAssign={(userId) => assignCover.mutate({ shiftId: coverShift.id, userId })}
            />
          ) : null
        }
        staff={data.staff}
        areas={data.areas}
        rates={rates}
        saving={createShift.isPending || updateShift.isPending}
        deleting={deleteShift.isPending}
        isOwner={isOwner}
        onClose={() => setDrawer(null)}
        onDelete={(id) => deleteShift.mutate(id)}
        onSave={(values, force) => {
          if (values.id === null) {
            createShift.mutate({ form: values, force });
          } else {
            updateShift.mutate({
              id: values.id,
              changes: {
                date: values.date,
                startTime: values.startTime,
                endTime: values.endTime,
                userId: values.userId,
                areaId: values.areaId,
                breakMinutes: values.breakMinutes,
                notes: values.notes || null,
                locked: values.locked,
              },
              force,
            });
          }
          setDrawer(null);
        }}
      />

      {dialogs}
    </div>
  );
}
